/**
 * The deposit-link expiry sweep rule (tv-2na.10.11) and the link's appointment
 * (tv-2na.12.13) — ONE pure rule the web store mirror (apps/web depositFlow
 * reconcileDepositHolds) and the server sweep (packages/db sweeps.ts) both apply,
 * so the browser and the DB can never disagree on which links lapse.
 *
 * A link is sweepable when it is:
 *   - still `pending` (unpaid);
 *   - not a pay-at-studio hold (that is never on the link clock);
 *   - carrying NO money: no amount_paid, no ledger balance and no paid top-up
 *     instalment on a LIVE plan (planProtectsLink) — the 24h link expiry never
 *     applies to a part-paid deposit (board 02, wave 4 / 5a5fb54);
 *   - not on a booking that has already arrived (checked-in / in-chair /
 *     completed): the desk is settling it in person;
 *   - past its `expiresAt`.
 */
import { ledgerPaidAud, type CancelSplitLedgerEntry } from './cancelSplit';
import { isLiveBooking } from './bookingStates';

export interface LinkSweepDeposit {
  status: string;
  expiresAt: string;
  amountAud: number;
  amountPaid?: number;
  ledger?: readonly CancelSplitLedgerEntry[];
  payAtStudio?: boolean;
}

export interface LinkSweepContext {
  /** A top-up plan that protects the link (planProtectsLink). */
  hasPaidInstalment?: boolean;
  /** Status of the booking the link points at (heldBookingId ?? bookingId), if any. */
  bookingStatus?: string;
}

/** Booking statuses where the client is (or was) in the studio. */
const ARRIVED: ReadonlySet<string> = new Set(['checked-in', 'in-chair', 'completed']);

/** Money already on a deposit: the larger of its ledger net and amount_paid. */
export function depositMoneyInAud(d: Pick<LinkSweepDeposit, 'ledger' | 'amountPaid' | 'status' | 'amountAud'>): number {
  const net = ledgerPaidAud({
    ...(d.ledger ? { ledger: [...d.ledger] } : {}),
    ...(d.amountPaid !== undefined ? { amountPaid: d.amountPaid } : {}),
    status: d.status,
    amountAud: d.amountAud,
  });
  return Math.max(net, d.amountPaid ?? 0);
}

/**
 * Does this top-up plan's paid instalment protect its link from the sweep? Only
 * a LIVE plan on a LIVE booking does: a cancelled plan, or one whose booking is
 * cancelled / superseded / no-show, protects nothing (a safety net — cancels
 * cancel the plan too). Money actually on the deposit still protects it
 * regardless (depositMoneyInAud). The sweep never writes plans.
 */
export function planProtectsLink(
  plan: { status: string; hasPaidInstalment: boolean },
  bookingStatus?: string,
): boolean {
  if (!plan.hasPaidInstalment || plan.status === 'cancelled') return false;
  return bookingStatus === undefined || isLiveBooking({ status: bookingStatus });
}

/** True when the link expiry sweep should expire this deposit at `nowIso`. */
export function isLinkSweepable(d: LinkSweepDeposit, nowIso: string, ctx: LinkSweepContext = {}): boolean {
  if (d.status !== 'pending') return false;
  if (d.payAtStudio) return false;
  if (ctx.hasPaidInstalment) return false;
  if (depositMoneyInAud(d) > 0) return false;
  if (ctx.bookingStatus && ARRIVED.has(ctx.bookingStatus)) return false;
  return Date.parse(d.expiresAt) <= Date.parse(nowIso);
}

export interface AppointmentDeposit {
  heldBookingId?: string;
  bookingId?: string;
  discussedSlot?: { startIso: string };
}

/**
 * The appointment a deposit is for: its held booking, else the booking it is
 * linked to (a part-paid / firm link carries only `bookingId`), when that booking
 * is live; else the pinned slot. Undefined = nothing resolves — callers deciding
 * a 7-day question must fail CLOSED (treat it as inside 7 days).
 */
export function depositAppointmentIso(
  d: AppointmentDeposit,
  bookings: Readonly<Record<string, { status: string; start: string } | undefined>>,
): string | undefined {
  const id = d.heldBookingId ?? d.bookingId;
  const b = id ? bookings[id] : undefined;
  if (b && isLiveBooking(b)) return b.start;
  return d.discussedSlot?.startIso;
}
